import React, { useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Icon } from 'antd';
import PropTypes from 'prop-types';

import Card from 'components/card/Card';
import DateLineItem from 'components/dateLineItem/DateLineItem';
import Link from 'components/link/Link';
import TooltipInfoModal from 'components/modals/TooltipInfoModal';
import { AppRoutes } from 'routes';
import { formatAccountNumber } from 'utils/Formatter';

const AccountSummary = (props) => {
    const { formatMessage } = useIntl();
    const [modalType, setModalType] = useState(null);

    return (
        <section aria-label="account summary" className="account-summary">
            <Card>
                <div className="card-line-item">
                    <div className="account-summary-header">
                        <div>
                            <h5 className="merchant-name">{props.merchantName}</h5>
                            <p className="account-number">{formatAccountNumber(props.accountNumber, true)}</p>
                        </div>
                        <div>
                            <p className="balance-label">
                                <FormattedMessage id="pages.account.summary.account.balance" />
                            </p>
                            <p className="balance-value">
                                <FormattedNumber
                                    currency="CAD"
                                    currencyDisplay="narrowSymbol"
                                    style="currency"
                                    value={props.accountBalance}
                                />
                            </p>
                        </div>
                        <div className="summary-item">
                            <p>
                                <FormattedMessage id="pages.account.summary.payment.due.date" />
                            </p>
                            <p className="summary-item-value">
                                <DateLineItem value={props.paymentDueDate} />
                            </p>
                        </div>
                        <div className="summary-item">
                            <p>
                                <FormattedMessage id="pages.account.summary.minimum.payment" />
                            </p>
                            <p className="summary-item-value">
                                <FormattedNumber
                                    currency="CAD"
                                    currencyDisplay="narrowSymbol"
                                    style="currency"
                                    value={props.minimumPayment}
                                />
                            </p>
                        </div>
                    </div>
                </div>
                <div className="card-line-item">
                    <div className="summary-item">
                        <div className="summary-label-icon">
                            <p>
                                <FormattedMessage id="pages.account.summary.available.credit" />
                            </p>
                            <button
                                aria-live="polite"
                                className="tooltip-button"
                                onClick={() => setModalType('availableCredit')}
                                role="tooltip"
                                tabIndex={0}
                                type="button"
                            >
                                <Icon className="popup-hint" id="info-icon" type="question-circle" />
                            </button>
                        </div>
                        <p className="summary-item-value">
                            <FormattedNumber
                                currency="CAD"
                                currencyDisplay="narrowSymbol"
                                style="currency"
                                value={props.availableCredit}
                            />
                        </p>
                    </div>
                    <div className="summary-item">
                        <div className="summary-label-icon">
                            <p>
                                <FormattedMessage id="pages.account.summary.credit.limit" />
                            </p>
                            <button
                                aria-live="polite"
                                className="tooltip-button"
                                onClick={() => setModalType('creditLimit')}
                                role="tooltip"
                                tabIndex={0}
                                type="button"
                            >
                                <Icon className="popup-hint" id="info-icon" type="question-circle" />
                            </button>
                        </div>
                        <p className="summary-item-value">
                            <FormattedNumber
                                currency="CAD"
                                currencyDisplay="narrowSymbol"
                                style="currency"
                                value={props.creditLimit}
                            />
                        </p>
                    </div>
                </div>
                <div className="card-line-item">
                    <div className="summary-item">
                        <p>
                            <FormattedMessage id="pages.account.summary.last.payment.date" />
                        </p>
                        <p className="summary-item-value">
                            <DateLineItem value={props.lastPaymentDate} />
                        </p>
                    </div>
                    <div className="summary-item">
                        <p>
                            <FormattedMessage id="pages.account.summary.last.amount.paid" />
                        </p>
                        <p className="summary-item-value">
                            <FormattedNumber
                                currency="CAD"
                                currencyDisplay="narrowSymbol"
                                style="currency"
                                value={props.lastPaymentAmount}
                            />
                        </p>
                    </div>
                </div>
                <div className="card-line-item">
                    <div className="summary-link">
                        <Link
                            aria-label={formatMessage({
                                id: 'pages.account.summary.plan.details.link',
                            })}
                            to={AppRoutes.planPage}
                        >
                            <FormattedMessage id="pages.account.summary.plan.details.link" />
                        </Link>
                    </div>
                </div>
                <div className="card-line-item">
                    <div className="summary-link">
                        <Link
                            aria-label={formatMessage({
                                id: 'pages.account.summary.transaction.details.link',
                            })}
                            to={AppRoutes.transactionHistoryPage}
                        >
                            <FormattedMessage id="pages.account.summary.transaction.details.link" />
                        </Link>
                    </div>
                </div>
                <TooltipInfoModal
                    onOk={() => setModalType(null)}
                    type={modalType}
                    visible={modalType === 'availableCredit' || modalType === 'creditLimit'}
                />
            </Card>
        </section>
    );
};

AccountSummary.propTypes = {
    accountBalance: PropTypes.number,
    accountNumber: PropTypes.string.isRequired,
    availableCredit: PropTypes.number,
    creditLimit: PropTypes.number,
    lastPaymentAmount: PropTypes.number,
    lastPaymentDate: PropTypes.string,
    merchantName: PropTypes.string.isRequired,
    minimumPayment: PropTypes.number,
    paymentDueDate: PropTypes.string,
};

export default AccountSummary;
